import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { globalSearch } from "../../Redux/Actions/action";
import { BucketURL } from "../../ApiSetup/Api";
import ErrorHandler from "../../Utils/handler";

const RECENT_KEY = "ppsk_recent_searches";
const MAX_RECENT = 8;
const DEBOUNCE_MS = 300;

const GROUP_ICONS = {
  person: PersonOutlineIcon,
  description: DescriptionOutlinedIcon,
  payments: PaymentsOutlinedIcon,
  support: SupportAgentOutlinedIcon,
  gavel: GavelOutlinedIcon,
  business: BusinessOutlinedIcon,
  receipt: ReceiptOutlinedIcon,
};

const getRecentSearches = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveRecentSearch = (query) => {
  if (!query?.trim()) return;
  const trimmed = query.trim();
  const prev = getRecentSearches().filter((q) => q !== trimmed);
  const next = [trimmed, ...prev].slice(0, MAX_RECENT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
};

const highlightText = (text, query) => {
  if (!text || !query) return text;
  const words = String(query).trim().split(/\s+/).filter((w) => w.length >= 2);
  if (!words.length) return text;

  const str = String(text);
  const lower = str.toLowerCase();
  const ranges = [];

  words.forEach((word) => {
    const wLower = word.toLowerCase();
    let start = 0;
    while (start < lower.length) {
      const idx = lower.indexOf(wLower, start);
      if (idx === -1) break;
      ranges.push([idx, idx + word.length]);
      start = idx + word.length;
    }
  });

  if (!ranges.length) return text;

  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  ranges.forEach(([s, e]) => {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) {
      last[1] = Math.max(last[1], e);
    } else {
      merged.push([s, e]);
    }
  });

  const parts = [];
  let cursor = 0;
  merged.forEach(([s, e], i) => {
    if (cursor < s) parts.push(str.slice(cursor, s));
    parts.push(
      <Box
        key={`${s}-${e}-${i}`}
        component="mark"
        sx={{ bgcolor: "#eef0ff", color: "inherit", px: 0.25, borderRadius: 0.5 }}
      >
        {str.slice(s, e)}
      </Box>
    );
    cursor = e;
  });
  if (cursor < str.length) parts.push(str.slice(cursor));
  return <>{parts}</>;
};

const flattenResults = (groups) => {
  const flat = [];
  (groups || []).forEach((group) => {
    (group.results || []).forEach((result) => {
      flat.push({ ...result, groupType: group.type, groupLabel: group.label });
    });
  });
  return flat;
};

const GlobalHeaderSearch = ({ variant = "desktop" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const anchorRef = useRef(null);
  const requestIdRef = useRef(0);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState(getRecentSearches);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isOverlay = variant === "mobile" || isMobile;
  const showPanel = isOverlay ? mobileOpen : open;

  const flatResults = useMemo(() => flattenResults(data?.groups), [data]);
  const normalizedQuery = data?.query?.normalized || query.trim();

  const runSearch = useCallback(
    async (searchText) => {
      const trimmed = (searchText || "").trim();
      if (trimmed.length < 2) {
        setData(null);
        setLoading(false);
        return;
      }

      const reqId = ++requestIdRef.current;
      setLoading(true);

      try {
        const result = await dispatch(globalSearch({ search: trimmed, limit: 5 }));
        if (reqId !== requestIdRef.current) return;

        if (!ErrorHandler(result, false)) {
          setData({ groups: [], totalCount: 0, query: { normalized: trimmed } });
          toast.error("Search failed. Please try again.");
          return;
        }

        const payload = result?.payload?.data;
        if (payload?.errors?.length) {
          toast.warning(`Some results could not be loaded (${payload.errors.length} groups)`);
        }
        if (payload && (payload.groups || payload.query)) {
          setData(payload);
        } else {
          setData({ groups: [], totalCount: 0, query: { normalized: trimmed } });
        }
        setHighlightIdx(-1);
      } catch {
        if (reqId === requestIdRef.current) {
          setData({ groups: [], totalCount: 0, query: { normalized: trimmed } });
          toast.error("Search failed. Please try again.");
        }
      } finally {
        if (reqId === requestIdRef.current) setLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (!showPanel && !query.trim()) return undefined;
    const timer = setTimeout(() => {
      if (query.trim().length >= 2) runSearch(query);
      else {
        setData(null);
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, runSearch, showPanel]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (isOverlay) {
          setMobileOpen(true);
        } else {
          inputRef.current?.focus();
          setOpen(true);
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOverlay]);

  const handleSelect = (result, searchQuery) => {
    if (!result?.path) return;
    saveRecentSearch(searchQuery || query);
    setRecent(getRecentSearches());
    setOpen(false);
    setMobileOpen(false);
    setQuery("");
    setData(null);
    navigate(result.path);
  };

  const handleEnter = () => {
    if (highlightIdx >= 0 && flatResults[highlightIdx]) {
      handleSelect(flatResults[highlightIdx], normalizedQuery);
      return;
    }
    if (flatResults.length === 1) {
      handleSelect(flatResults[0], normalizedQuery);
      return;
    }
    const topGroup = data?.groups?.[0];
    if (topGroup?.viewAllPath) {
      saveRecentSearch(normalizedQuery);
      setRecent(getRecentSearches());
      navigate(topGroup.viewAllPath);
      setOpen(false);
      setMobileOpen(false);
      setQuery("");
      setData(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, flatResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleEnter();
    } else if (e.key === "Escape") {
      setOpen(false);
      setMobileOpen(false);
      inputRef.current?.blur();
    }
  };

  const renderResultRow = (result, idx, groupLabel) => {
    const iconKey = {
      members: "person",
      registration: "description",
      transactions: "payments",
      tickets: "support",
      claims: "gavel",
      organisations: "business",
      expenses: "receipt",
    }[result.groupType];
    const GroupIcon = GROUP_ICONS[iconKey] || PersonOutlineIcon;
    const isHighlighted = idx === highlightIdx;

    return (
      <Box
        key={`${result.groupType}-${result.id}`}
        onClick={() => handleSelect(result, normalizedQuery)}
        onMouseEnter={() => setHighlightIdx(idx)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 1,
          cursor: "pointer",
          bgcolor: isHighlighted ? "#f0f2ff" : "transparent",
          "&:hover": { bgcolor: "#f0f2ff" },
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        {result.avatar ? (
          <Avatar src={BucketURL + result.avatar} sx={{ width: 36, height: 36 }} />
        ) : (
          <Avatar sx={{ width: 36, height: 36, bgcolor: "#eef0ff", color: "#3E4094" }}>
            <GroupIcon fontSize="small" />
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>
              {highlightText(result.title, normalizedQuery)}
            </Typography>
            {result.badge?.text && (
              <Chip
                label={result.badge.text}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 600,
                  bgcolor: `${result.badge.color}18`,
                  color: result.badge.color,
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
          <Typography noWrap sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}>
            {highlightText(result.subtitle, normalizedQuery)}
          </Typography>
          {result.matchedField && (
            <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.25 }}>
              Matched on {result.matchedField}
              {groupLabel ? ` · ${groupLabel}` : ""}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderDropdownContent = () => {
    const showRecent = !query.trim() && recent.length > 0;

    if (showRecent) {
      return (
        <Box sx={{ py: 1 }}>
          <Typography sx={{ px: 1.5, py: 0.5, fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>
            Recent searches
          </Typography>
          {recent.map((item) => (
            <Box
              key={item}
              className="touch-target"
              onClick={() => {
                setQuery(item);
                runSearch(item);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 1.25,
                minHeight: 44,
                cursor: "pointer",
                "@media (hover: hover)": {
                  "&:hover": { bgcolor: "#f8fafc" },
                },
                "&:active": { bgcolor: "#f1f5f9" },
              }}
            >
              <HistoryIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
              <Typography sx={{ fontSize: 13 }}>{item}</Typography>
            </Box>
          ))}
        </Box>
      );
    }

    if (loading && !data) {
      return (
        <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={22} />
        </Box>
      );
    }

    if (query.trim().length >= 2 && data && (!data.groups || data.groups.length === 0)) {
      return (
        <Box sx={{ p: 2.5, textAlign: "center" }}>
          <SearchOffOutlinedIcon sx={{ fontSize: 32, color: "#cbd5e1", mb: 1 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#475569" }}>
            No results for &ldquo;{normalizedQuery}&rdquo;
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.5 }}>
            Try PPSK ID, mobile number, or member name
          </Typography>
        </Box>
      );
    }

    if (!data?.groups?.length) return null;

    let flatIdx = 0;
    return (
      <Box>
        {data.query?.hint && (
          <Typography sx={{ px: 1.5, py: 0.75, fontSize: 11, color: "#3E4094", bgcolor: "#f8f9ff", borderBottom: "1px solid #eef0ff" }}>
            {data.query.hint}
            {data.timingMs ? ` · ${data.timingMs}ms` : ""}
          </Typography>
        )}
        {data.groups.map((group) => {
          const GroupIcon = GROUP_ICONS[group.icon] || PersonOutlineIcon;
          return (
            <Box key={group.type}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.75,
                  bgcolor: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <GroupIcon sx={{ fontSize: 14, color: "#3E4094" }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                    {group.label}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>({group.totalCount})</Typography>
                </Box>
                {(group.hasMore || group.totalCount > (group.results?.length || 0)) && group.viewAllPath && (
                  <Typography
                    onClick={() => {
                      saveRecentSearch(normalizedQuery);
                      navigate(group.viewAllPath);
                      setOpen(false);
                      setMobileOpen(false);
                      setQuery("");
                    }}
                    sx={{ fontSize: 11, color: "#3E4094", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}
                  >
                    View all
                  </Typography>
                )}
              </Box>
              {group.results.map((result) => {
                const row = renderResultRow({ ...result, groupType: group.type }, flatIdx, group.label);
                flatIdx += 1;
                return row;
              })}
            </Box>
          );
        })}
      </Box>
    );
  };

  const searchField = (
    <TextField
      inputRef={inputRef}
      value={query}
      onChange={(e) => {
        setQuery(e.target.value);
        setOpen(true);
      }}
      onFocus={() => {
        setOpen(true);
        setRecent(getRecentSearches());
      }}
      onKeyDown={handleKeyDown}
      placeholder={isOverlay ? "Search members, PPSK ID, mobile, transactions..." : "Search anything... (⌘K)"}
      size="small"
      fullWidth
      autoFocus={isOverlay && mobileOpen}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 20, color: query ? "#3E4094" : "#94a3b8" }} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            {loading && <CircularProgress size={16} sx={{ mr: query ? 0.5 : 0 }} />}
            {query && (
              <IconButton
                onClick={() => {
                  setQuery("");
                  setData(null);
                  inputRef.current?.focus();
                }}
                aria-label="Clear search"
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </InputAdornment>
        ),
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          bgcolor: "#fff",
          minHeight: 44,
          fontSize: 14,
          "& fieldset": { borderColor: "#e2e8f0" },
          "&:hover fieldset": { borderColor: "#3E4094" },
          "&.Mui-focused fieldset": {
            borderColor: "#3E4094",
            boxShadow: "0 0 0 3px #3E409418",
          },
        },
      }}
    />
  );

  if (isOverlay && !mobileOpen) {
    return (
      <IconButton
        onClick={() => setMobileOpen(true)}
        sx={{ color: "#3E4094", minWidth: 44, minHeight: 44 }}
        aria-label="Open search"
      >
        <SearchIcon />
      </IconButton>
    );
  }

  if (isOverlay && mobileOpen) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: theme.zIndex.modal + 2,
          bgcolor: "#fff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, borderBottom: "1px solid #e2e8f0", paddingTop: "calc(12px + env(safe-area-inset-top))" }}>
          <Box sx={{ flex: 1 }}>{searchField}</Box>
          <IconButton
            onClick={() => { setMobileOpen(false); setQuery(""); setData(null); }}
            sx={{ minWidth: 44, minHeight: 44 }}
            aria-label="Close search"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", overscrollBehavior: "contain" }}>{renderDropdownContent()}</Box>
      </Box>
    );
  }

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ flex: 1, maxWidth: 420, mx: { sm: 1, md: 2 } }}>
        {searchField}
        <Popper
          open={showPanel && (open || loading || !!data || (!query && recent.length > 0))}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: theme.zIndex.modal + 1, width: anchorRef.current?.offsetWidth || 400 }}
        >
          <Paper
            elevation={8}
            sx={{
              mt: 0.5,
              maxHeight: 480,
              overflow: "auto",
              borderRadius: 2,
              border: "1px solid #e2e8f0",
            }}
          >
            {renderDropdownContent()}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalHeaderSearch;
